import { useMemo, useRef, useState, type PointerEvent } from 'react'
import { DateTime } from 'luxon'
import { bordesDeVigencia } from '@practiclinic/core'
import {
  db,
  type AsignacionEspacioLocal,
  type RecursoAfinidadLocal,
  type RecursoHorarioLocal,
  type RecursoLocal,
} from '../datos/db.js'
import { sincronizar } from '../datos/sync.js'
import { fechaCorta, hoy, hora } from '../datos/formato.js'
import { Boton } from '../comunes/Boton.js'
import { Panel } from '../comunes/Panel.js'
import {
  DIAS,
  aMinutos,
  aReloj,
  asignacionCubreFecha,
  bloqueInicial,
  diasDeLaSemana,
  elegiblesDeCelda,
  rangoHoras,
  type PersonaElegible,
} from './elegibles.js'

/**
 * Plantilla semanal por consultorio (edición).
 *
 * Un consultorio, una grilla días × horas, y clic en una celda libre para
 * elegir quién la cubre. El bloque se pinta con el horario completo de la
 * persona elegida y se recorta con sus bordes antes de guardar.
 *
 * **Escritura: excepción declarada a "la UI lee sólo de Dexie" (§5.5).** Crear,
 * publicar y cancelar son trabajo de admin (o recepción autorizada) con
 * conexión, como ya lo documentaba `Asignaciones.tsx`. Los tres endpoints son
 * los existentes; después de cada escritura se llama `sincronizar()` y la UI
 * se repinta sola por `useLiveQuery`. Los errores que devuelve el servidor
 * (los cuatro mensajes de T28) se muestran tal cual.
 */

const API = '/api'
/** Píxeles por hora de grilla: el bloque de una hora mide 56 px. */
const PX_POR_HORA = 56
/** Snap de 15 minutos al arrastrar los bordes del bloque nuevo. */
const SNAP = 15

interface BorradorEnGrilla {
  personaId: string
  nombre: string
  diaSemana: number
  desde: number
  hasta: number
  /** El horario de la persona que limita los bordes arrastrables. */
  limite: { desde: number; hasta: number }
  /** Cuando el clic fue sobre un bloque publicado, la franja que se reemplaza. */
  reemplazaId: string | null
}

type CeldaClikeada = { diaSemana: number; horaMin: number; reemplazaId: string | null }

interface CitaAfectada {
  paciente: string
  cuando: string
}

interface CitasaModal {
  asignacion: AsignacionEspacioLocal
  citas: CitaAfectada[]
}

const ESTADOS_TERMINALES = ['cancelada', 'no_asistio', 'pendiente_reprogramar']

async function pedir(
  ruta: string,
  metodo: string,
  cuerpo?: Record<string, unknown>,
): Promise<{ id?: string; mensaje?: string }> {
  const init: RequestInit = { method: metodo, credentials: 'include' }
  if (cuerpo !== undefined) {
    init.headers = { 'content-type': 'application/json' }
    init.body = JSON.stringify(cuerpo)
  }
  const res = await fetch(`${API}${ruta}`, init)
  const detalle = (await res.json().catch(() => ({}))) as { id?: string; mensaje?: string }
  if (!res.ok) throw new Error(detalle.mensaje ?? 'No se pudo completar la operación')
  return detalle
}

/**
 * Las citas que una franja publicada pisa: `cita_tramo` con
 * `asignacion_espacio_id` de esa franja, de ahora en adelante y cuya cita no
 * esté en un estado terminal. Es lo que protege el trigger
 * `asignacion_tramos_vigentes_check`.
 */
async function citasQuePisan(asignacionId: string): Promise<CitaAfectada[]> {
  const ahora = new Date().toISOString()
  const tramos = (
    await db.cita_tramo.filter((t) => t.asignacion_espacio_id === asignacionId).toArray()
  ).filter((t) => !t.deleted_at && t.inicio >= ahora)
  if (tramos.length === 0) return []

  const citas = await db.cita.bulkGet([...new Set(tramos.map((t) => t.cita_id))])
  const vivas = citas.filter(
    (c): c is NonNullable<typeof c> =>
      c !== undefined && !c.deleted_at && !ESTADOS_TERMINALES.includes(c.estado),
  )
  if (vivas.length === 0) return []

  const clientes = await db.cliente.bulkGet(vivas.map((c) => c.cliente_id))
  const porCliente = new Map(clientes.filter(Boolean).map((c) => [c!.id, c!.nombre]))

  return tramos
    .filter((t) => vivas.some((c) => c.id === t.cita_id))
    .sort((a, b) => a.inicio.localeCompare(b.inicio))
    .map((t) => {
      const cita = vivas.find((c) => c.id === t.cita_id)
      return {
        paciente: cita ? (porCliente.get(cita.cliente_id) ?? 'Paciente') : 'Paciente',
        cuando: `${fechaCorta(t.inicio)} · ${hora(t.inicio)}`,
      }
    })
}

export function PlantillaSemanal({
  espacios,
  personas,
  horarios,
  afinidades,
  asignaciones,
  sucursalId,
}: {
  espacios: RecursoLocal[]
  personas: RecursoLocal[]
  horarios: RecursoHorarioLocal[]
  afinidades: RecursoAfinidadLocal[]
  asignaciones: AsignacionEspacioLocal[]
  sucursalId: string | null
}) {
  const [espacioId, setEspacioId] = useState(espacios[0]?.id ?? '')
  const [selector, setSelector] = useState<CeldaClikeada | null>(null)
  const [nuevo, setNuevo] = useState<BorradorEnGrilla | null>(null)
  const [citasModal, setCitasModal] = useState<CitasaModal | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [guardando, setGuardando] = useState(false)

  const grillaRef = useRef<HTMLDivElement | null>(null)
  const arrastre = useRef<'desde' | 'hasta' | null>(null)

  const espacioSel = espacioId || espacios[0]?.id || ''
  const dias = diasDeLaSemana(horarios)
  const horas = useMemo(() => {
    const { desde, hasta } = rangoHoras(horarios)
    const lista: number[] = []
    for (let h = desde; h < hasta; h++) lista.push(h)
    return { desde, lista }
  }, [horarios])

  const hoyDia = hoy()

  // Las filas de este consultorio que todavía importan: publicadas y borradores
  // cuya vigencia cubre hoy.
  const activas = useMemo(
    () =>
      asignaciones.filter(
        (a) =>
          a.espacio_id === espacioSel &&
          a.estado !== 'cancelada' &&
          !a.deleted_at &&
          asignacionCubreFecha(a, hoyDia),
      ),
    [asignaciones, espacioSel, hoyDia],
  )

  const vigenteDesde = useMemo(() => {
    const fechas = activas
      .filter((a) => a.estado === 'publicada')
      .map((a) => bordesDeVigencia(a.vigencia).vigenciaDesde.toISOString().slice(0, 10))
    if (fechas.length === 0) return null
    return fechas.sort()[0]!
  }, [activas])

  const nombreEspacio = espacios.find((e) => e.id === espacioSel)?.nombre ?? ''

  function abrirCelda(dia: number, h: number) {
    // Celdas ocupadas por un bloque existente no abren el buscador: se editan
    // clickeando el bloque.
    const cubierto = activas.some(
      (a) => a.dia_semana === dia && aMinutos(a.desde) <= h * 60 && h * 60 < aMinutos(a.hasta),
    )
    if (cubierto) return
    setNuevo(null)
    setSelector({ diaSemana: dia, horaMin: h * 60, reemplazaId: null })
  }

  function abrirEdicion(dia: number, horaMin: number, asignacion: AsignacionEspacioLocal) {
    setNuevo(null)
    setSelector({ diaSemana: dia, horaMin, reemplazaId: asignacion.id })
  }

  function elegirPersona(elegida: PersonaElegible) {
    if (!selector || elegida.motivo) return
    const inicial = bloqueInicial(
      selector.diaSemana,
      selector.horaMin,
      horarios.filter((h) => h.recurso_id === elegida.personaId),
    )
    if (!inicial) return
    setNuevo({
      personaId: elegida.personaId,
      nombre: elegida.nombre,
      diaSemana: selector.diaSemana,
      desde: inicial.desde,
      hasta: inicial.hasta,
      limite: inicial,
      reemplazaId: selector.reemplazaId,
    })
    setSelector(null)
  }

  // ── Arrastre de los bordes del bloque nuevo ────────────────────────

  function tomarBorde(extremo: 'desde' | 'hasta') {
    return (ev: PointerEvent<HTMLDivElement>) => {
      arrastre.current = extremo
      ev.currentTarget.setPointerCapture(ev.pointerId)
    }
  }

  function moverBorde(ev: PointerEvent<HTMLDivElement>) {
    if (!nuevo || !arrastre.current || !grillaRef.current) return
    const top = grillaRef.current.getBoundingClientRect().top
    const minuto = ((ev.clientY - top) / PX_POR_HORA) * 60 + horas.desde * 60
    const propuesto =
      arrastre.current === 'desde'
        ? Math.floor(minuto / SNAP) * SNAP
        : Math.ceil(minuto / SNAP) * SNAP
    const limite = nuevo.limite
    const desde = Math.max(limite.desde, Math.min(nuevo.hasta - SNAP, propuesto))
    const hasta = Math.min(limite.hasta, Math.max(nuevo.desde + SNAP, propuesto))
    setNuevo({ ...nuevo, desde, hasta })
  }

  function soltarBorde() {
    arrastre.current = null
  }

  // ── Escritura ──────────────────────────────────────────────────────

  async function guardarBorrador() {
    if (!nuevo || !sucursalId) return
    setGuardando(true)
    setError(null)
    try {
      await pedir('/config/asignaciones', 'POST', {
        sucursalId,
        personaId: nuevo.personaId,
        espacioId: espacioSel,
        diaSemana: nuevo.diaSemana,
        desde: aReloj(nuevo.desde),
        hasta: aReloj(nuevo.hasta),
      })
      await sincronizar()
      setNuevo(null)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo guardar el borrador')
    } finally {
      setGuardando(false)
    }
  }

  async function publicar(id: string) {
    setGuardando(true)
    setError(null)
    try {
      await pedir(`/config/asignaciones/${id}/publicar`, 'POST')
      await sincronizar()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo publicar la franja')
    } finally {
      setGuardando(false)
    }
  }

  async function cancelar(id: string) {
    setGuardando(true)
    setError(null)
    try {
      await pedir(`/config/asignaciones/${id}/cancelar`, 'POST')
      await sincronizar()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo cancelar la franja')
    } finally {
      setGuardando(false)
    }
  }

  /** La franja nueva se crea y se publica directo (del bloque recién elegido). */
  async function guardarYPublicar() {
    if (!nuevo || !sucursalId) return
    setGuardando(true)
    setError(null)
    try {
      const { id } = await pedir('/config/asignaciones', 'POST', {
        sucursalId,
        personaId: nuevo.personaId,
        espacioId: espacioSel,
        diaSemana: nuevo.diaSemana,
        desde: aReloj(nuevo.desde),
        hasta: aReloj(nuevo.hasta),
      })
      if (!id) throw new Error('No se pudo crear la franja')
      await pedir(`/config/asignaciones/${id}/publicar`, 'POST')
      await sincronizar()
      setNuevo(null)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo publicar la franja')
      // Si la publicación falló, la fila quedó como borrador visible.
      await sincronizar().catch(() => undefined)
      setNuevo(null)
    } finally {
      setGuardando(false)
    }
  }

  /**
   * Editar un bloque publicado = cancelarlo y crear otro (no hay PATCH).
   * Antes de cancelar: si pisa citas futuras, modal con la lista y guardado
   * bloqueado. Si no: cancelar el viejo → crear el nuevo → publicarlo.
   */
  async function reemplazar() {
    if (!nuevo || !nuevo.reemplazaId || !sucursalId) return
    const vieja = asignaciones.find(
      (a) => a.id === nuevo.reemplazaId && a.estado === 'publicada' && !a.deleted_at,
    )
    if (!vieja) {
      setError('La franja original ya no está vigente.')
      setNuevo(null)
      return
    }
    const citas = await citasQuePisan(vieja.id)
    if (citas.length > 0) {
      setCitasModal({ asignacion: vieja, citas })
      return
    }
    setGuardando(true)
    setError(null)
    try {
      await pedir(`/config/asignaciones/${vieja.id}/cancelar`, 'POST')
      const { id } = await pedir('/config/asignaciones', 'POST', {
        sucursalId,
        personaId: nuevo.personaId,
        espacioId: espacioSel,
        diaSemana: nuevo.diaSemana,
        desde: aReloj(nuevo.desde),
        hasta: aReloj(nuevo.hasta),
      })
      if (!id) throw new Error('No se pudo crear la nueva franja')
      await pedir(`/config/asignaciones/${id}/publicar`, 'POST')
      await sincronizar()
      setNuevo(null)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo reemplazar la franja')
      // Si la publicación falló, el nuevo quedó como borrador visible.
      await sincronizar().catch(() => undefined)
      setNuevo(null)
    } finally {
      setGuardando(false)
    }
  }

  async function accionDraftGuardado() {
    if (nuevo?.reemplazaId) await reemplazar()
    else await guardarYPublicar()
  }

  const elegidas = selector
    ? elegiblesDeCelda(
        selector.diaSemana,
        selector.horaMin,
        espacioSel,
        personas,
        horarios,
        afinidades,
        asignaciones,
        espacios,
      )
    : []

  const altoGrilla = (horas.lista.length * PX_POR_HORA).toString()

  return (
    <section>
      <header className="mb-4">
        <h2 className="txt-seccion">
          Plantilla semanal · vigente desde {vigenteDesde ? DateTime.fromISO(vigenteDesde).toFormat('dd/MM') : '—'}
        </h2>
        <p className="txt-secundario">
          El patrón que se repite cada semana para {nombreEspacio || 'el consultorio'}. Clic en una
          celda libre para asignar quién la cubre.
        </p>
      </header>

      <select
        value={espacioSel}
        onChange={(ev) => setEspacioId(ev.target.value)}
        className="h-9 rounded-lg border border-slate-300 bg-white px-2 text-sm"
        aria-label="Consultorio"
      >
        {espacios.map((e) => (
          <option key={e.id} value={e.id}>
            {e.nombre}
          </option>
        ))}
      </select>

      {error && (
        <p role="alert" className="mt-4 rounded-lg bg-rose-50 p-3 text-sm text-rose-800">
          {error}
        </p>
      )}

      <div className="mt-4 overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <div className="flex min-w-[720px]">
          {/* Eje de horas */}
          <div className="w-16 shrink-0 border-r border-slate-200">
            <div className="h-10" />
            {horas.lista.map((h) => (
              <div
                key={h}
                className="flex items-start justify-end pr-2 text-xs text-slate-400"
                style={{ height: PX_POR_HORA }}
              >
                <span className="mt-[-6px] tabular-nums">
                  {String(h).padStart(2, '0')}:00
                </span>
              </div>
            ))}
          </div>

          {/* Columnas por día */}
          {dias.map((dia, idx) => (
            <div key={`${dia}-${idx}`} className="min-w-0 flex-1 border-r border-slate-200 last:border-r-0">
              <div className="border-b border-slate-200 px-2 py-2.5 text-center text-sm font-medium text-slate-700">
                {DIAS[dia]}
              </div>
              <div
                ref={grillaRef}
                className="relative cursor-pointer"
                style={{ height: `${altoGrilla}px` }}
                onPointerMove={moverBorde}
                onPointerUp={soltarBorde}
                onPointerCancel={soltarBorde}
              >
                {horas.lista.map((h) => (
                  <div
                    key={h}
                    className="absolute left-0 right-0 border-b border-slate-100"
                    style={{ top: ((h - horas.desde) * PX_POR_HORA).toString(), height: PX_POR_HORA }}
                    onClick={() => abrirCelda(dia, h)}
                  />
                ))}

                {activas
                  .filter((a) => a.dia_semana === dia)
                  .map((a) => {
                    const desde = aMinutos(a.desde)
                    const hasta = aMinutos(a.hasta)
                    const top = ((desde - horas.desde * 60) / 60) * PX_POR_HORA
                    const alto = ((hasta - desde) / 60) * PX_POR_HORA
                    const esBorrador = a.estado === 'borrador'
                    return (
                      <div
                        key={a.id}
                        className={`absolute left-0.5 right-0.5 overflow-hidden rounded-md px-1.5 py-0.5 text-xs shadow-sm ${
                          esBorrador
                            ? 'border border-dashed border-teal-400 bg-teal-50 text-teal-800'
                            : 'cursor-pointer bg-teal-700 text-white hover:bg-teal-800'
                        }`}
                        style={{ top, height: Math.max(20, alto), zIndex: 10 }}
                        {...(!esBorrador ? { onClick: () => abrirEdicion(dia, desde, a) } : {})}
                      >
                        {esBorrador ? (
                          <div className="flex items-center justify-between gap-1">
                            <span className="truncate font-medium">{personaDe(a).nombre}</span>
                            <span className="flex shrink-0 gap-1">
                              <button
                                type="button"
                                onClick={(ev) => {
                                  ev.stopPropagation()
                                  void publicar(a.id)
                                }}
                                className="rounded bg-teal-700 px-1.5 py-0.5 text-[11px] font-medium text-white hover:bg-teal-800"
                              >
                                Publicar
                              </button>
                              <button
                                type="button"
                                onClick={(ev) => {
                                  ev.stopPropagation()
                                  void cancelar(a.id)
                                }}
                                className="rounded px-1 py-0.5 text-[11px] text-teal-600 hover:bg-teal-100"
                              >
                                Quitar
                              </button>
                            </span>
                          </div>
                        ) : (
                          <>
                            <p className="truncate font-medium">{personaDe(a).nombre}</p>
                            {alto > PX_POR_HORA && (
                              <p className="truncate tabular-nums text-teal-100">
                                {aReloj(desde)}–{aReloj(hasta)}
                              </p>
                            )}
                            {alto > PX_POR_HORA * 1.5 && (
                              <p className="mt-0.5 text-[11px] text-teal-100 underline underline-offset-2">
                                Editar
                              </p>
                            )}
                          </>
                        )}
                      </div>
                    )
                  })}

                {/* Bloque nuevo: el horario heredado, recortable con los bordes. */}
                {nuevo &&
                  nuevo.diaSemana === dia && (
                    <div
                      className="absolute left-0.5 right-0.5 z-20 rounded-md border-2 border-dashed border-indigo-500 bg-indigo-100/90 text-indigo-900 shadow-sm"
                      style={{
                        top: ((nuevo.desde - horas.desde * 60) / 60) * PX_POR_HORA,
                        height: Math.max(24, ((nuevo.hasta - nuevo.desde) / 60) * PX_POR_HORA),
                      }}
                    >
                      <div
                        className="absolute inset-x-1 -top-1 flex h-2 cursor-ns-resize items-center justify-center"
                        onPointerDown={tomarBorde('desde')}
                      >
                        <span className="h-0.5 w-8 rounded bg-indigo-500" />
                      </div>
                      <p className="mt-1 truncate px-1.5 font-medium">{nuevo.nombre}</p>
                      {nuevo.hasta - nuevo.desde >= 30 && (
                        <p className="truncate px-1.5 text-[11px] tabular-nums">
                          {aReloj(nuevo.desde)}–{aReloj(nuevo.hasta)}
                        </p>
                      )}
                      <div
                        className="absolute inset-x-1 -bottom-1 flex h-2 cursor-ns-resize items-center justify-center"
                        onPointerDown={tomarBorde('hasta')}
                      >
                        <span className="h-0.5 w-8 rounded bg-indigo-500" />
                      </div>
                    </div>
                  )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {nuevo && (
        <div className="mt-3 flex flex-wrap items-center gap-2 rounded-xl border border-indigo-200 bg-indigo-50 p-3">
          <span className="text-sm font-medium text-indigo-900">
            {nuevo.nombre} · {DIAS[nuevo.diaSemana]} {aReloj(nuevo.desde)}–{aReloj(nuevo.hasta)}
          </span>
          <span className="txt-meta">Arrastrá los bordes del bloque para recortarlo.</span>
          <div className="ml-auto flex gap-2">
            <Boton variante="primary" tamano="sm" disabled={guardando} onClick={() => void guardarBorrador()}>
              Guardar borrador
            </Boton>
            <Boton
              variante="secondary"
              tamano="sm"
              disabled={guardando}
              onClick={() => void accionDraftGuardado()}
            >
              Guardar y publicar
            </Boton>
            <Boton variante="ghost" tamano="sm" disabled={guardando} onClick={() => setNuevo(null)}>
              Descartar
            </Boton>
          </div>
        </div>
      )}

      {/* Buscador de persona para la celda */}
      {selector && (
        <Panel
          titulo={`¿Quién cubre ${DIAS[selector.diaSemana]} ${aReloj(selector.horaMin)}–${aReloj(selector.horaMin + 60)}?`}
          bajada={
            selector.reemplazaId
              ? 'Esto reemplaza la franja publicada de ese bloque.'
              : `Franjas libres de ${nombreEspacio}.`
          }
          onCerrar={() => setSelector(null)}
        >
          {elegidas.length === 0 ? (
            <p className="txt-secundario">No hay personas cargadas para asignar.</p>
          ) : (
            <ul className="space-y-2">
              {elegidas.map((e) => {
                const habilitada = e.motivo === null
                return (
                  <li key={e.personaId}>
                    <button
                      type="button"
                      disabled={!habilitada}
                      onClick={() => elegirPersona(e)}
                      className={`w-full rounded-lg border p-3 text-left transition ${
                        habilitada
                          ? 'border-teal-200 bg-white hover:border-teal-400 hover:bg-teal-50'
                          : 'cursor-not-allowed border-slate-200 bg-slate-50'
                      }`}
                    >
                      <p className={`text-sm font-medium ${habilitada ? 'text-slate-900' : 'text-slate-400'}`}>
                        {e.nombre}
                      </p>
                      {!habilitada && (
                        <p className="mt-0.5 text-xs text-rose-600">{e.motivo}</p>
                      )}
                    </button>
                  </li>
                )
              })}
            </ul>
          )}
        </Panel>
      )}

      {/* Modal de citas pisadas: guardar queda bloqueado hasta reprogramarlas. */}
      {citasModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-slate-900/20"
            onClick={() => setCitasModal(null)}
            aria-hidden="true"
          />
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Franja con citas agendadas"
            className="relative w-full max-w-md rounded-xl border border-slate-200 bg-white p-6 shadow-xl"
          >
            <h2 className="txt-seccion">La franja pisa citas agendadas</h2>
            <p className="txt-secundario mt-1">
              Para modificar esta franja hay que cancelarla, y no se puede cancelar mientras tenga
              citas agendadas. Reprogramalas o cancelalas primero.
            </p>
            <ul className="mt-3 max-h-52 divide-y divide-slate-100 overflow-y-auto">
              {citasModal.citas.map((c, i) => (
                <li key={i} className="flex items-baseline justify-between gap-2 py-2 text-sm">
                  <span className="font-medium text-slate-800">{c.paciente}</span>
                  <span className="shrink-0 tabular-nums text-slate-500">{c.cuando}</span>
                </li>
              ))}
            </ul>
            <div className="mt-4 flex justify-end gap-2">
              <Boton variante="primary" disabled>
                Guardar y publicar
              </Boton>
              <Boton variante="ghost" onClick={() => setCitasModal(null)}>
                Cerrar
              </Boton>
            </div>
          </div>
        </div>
      )}
    </section>
  )

  /** El nombre de la persona de una asignación, con respaldo por si no está en Dexie. */
  function personaDe(a: AsignacionEspacioLocal): RecursoLocal {
    return (
      personas.find((p) => p.id === a.persona_id) ?? {
        id: a.persona_id,
        nombre: 'Persona',
        recurso_tipo_id: '',
        sucursal_id: null,
        activo: true,
        updated_at: '',
        deleted_at: null,
      }
    )
  }
}